import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../../contexts/AppContext";
import { useModelOptions } from "../../hooks/useModelOptions";
import { buildLlmModelOptions } from "../../utils/modelRegistry";
import { getDataSets } from "../../services/data";

// Server-backed pickers for the dynamic node: the model field lists the built-ins plus the
// registered endpoints, and the dataset field lists the current client's datasets.

const fieldStyle = {
  width: "100%",
  padding: "5px 8px",
  fontSize: "12px",
  fontFamily: "inherit",
  color: "var(--main-font-color)",
  backgroundColor: "var(--main-bg)",
  border: "1px solid var(--main-border-color)",
  borderRadius: "5px",
  boxSizing: "border-box",
};

/**
 * Model name field: a select of the built-ins plus the client's registered endpoints, in the
 * same format as the dataset selector (empty "Select…" option, current value kept visible).
 */
export const ModelPicker = ({ value, onChange, placeholder }) => {
  const { selectedClient } = useContext(AppContext);
  const { models } = useModelOptions({
    clientId: selectedClient?.public_id ?? selectedClient?.id ?? null,
  });
  const options = buildLlmModelOptions(models);
  const stringValue = value === undefined || value === null ? "" : String(value);

  // Mirror the choice locally so the dropdown doesn't snap back while the node data round-trips.
  const [selected, setSelected] = useState(stringValue);
  useEffect(() => {
    setSelected(stringValue);
  }, [stringValue]);

  const hasCurrent = options.some((option) => option.value === selected);
  const finalOptions =
    hasCurrent || !selected
      ? [{ value: "", label: placeholder ?? "Select a model" }, ...options]
      : [{ value: "", label: placeholder ?? "Select a model" }, { value: selected, label: selected }, ...options];

  return (
    <select
      className="nodrag"
      style={fieldStyle}
      value={selected}
      onChange={(event) => {
        setSelected(event.target.value);
        onChange(event.target.value);
      }}
    >
      {finalOptions.map((option) => (
        <option key={option.value || "__none"} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

/**
 * Dataset field: a select of the current client's datasets (public id → name), keeping the
 * current value visible even if it isn't in the freshly loaded list.
 */
export const DatasetPicker = ({ value, onChange, placeholder }) => {
  const { selectedClient } = useContext(AppContext);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const clientId = selectedClient?.id;

    if (!clientId) {
      setOptions([]);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    getDataSets(clientId)
      .then((results) => {
        if (cancelled) return;
        const list = results?.data_sets ?? results?.datasets ?? [];
        if (Array.isArray(list)) {
          setOptions([
            { value: "", label: "Select a dataset" },
            ...list.map((dataset) => ({ value: dataset.public_id, label: dataset.name ?? dataset.public_id })),
          ]);
        } else {
          setOptions([]);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setOptions([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedClient?.id]);

  const stringValue = value === undefined || value === null ? "" : String(value);

  // Mirror the choice locally so the dropdown doesn't snap back while the node data round-trips.
  const [selected, setSelected] = useState(stringValue);
  useEffect(() => {
    setSelected(stringValue);
  }, [stringValue]);

  const hasCurrent = options.some((option) => option.value === selected);
  const finalOptions =
    hasCurrent || !selected ? options : [{ value: selected, label: selected }, ...options];

  return (
    <select
      className="nodrag"
      style={fieldStyle}
      value={selected}
      onChange={(event) => {
        setSelected(event.target.value);
        onChange(event.target.value);
      }}
    >
      {!selectedClient?.id && !loading ? <option value="">Select a client first</option> : null}
      {loading && finalOptions.length === 0 ? <option value="">Loading…</option> : null}
      {finalOptions.map((option) => (
        <option key={option.value || "__none"} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};
